import { useLocation, Link } from "wouter";
import { useEffect, useState } from "react";
import {
  Activity,
  Lightbulb,
  Bell,
  Sparkles,
  Eye,
  SearchCheck,
} from "lucide-react";
import { cn } from "@/lib/utils.js";
import {
  segmentedTabIdle,
  segmentedTabSelected,
  segmentedTabTrack,
} from "@/lib/status-styles.js";

/**
 * Asset navigation tabs component
 * Displays horizontal navigation tabs for asset-specific pages
 * @param {{ assetId: string }} props - Component props
 * @returns {JSX.Element} Asset navigation tabs component
 */
export default function AssetNavigationTabs({ assetId }) {
  const [location] = useLocation();
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Update path when location changes
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, [location]);

  // Encode assetId for URLs to handle special characters
  const encodedAssetId = encodeURIComponent(assetId);

  // Preserve query params (e.g. from=operating-points, page, perPage) when switching tabs so the back button works
  const queryString =
    typeof window !== "undefined" ? window.location.search : "";

  const navigationItems = [
    {
      name: "Asset Overview",
      path: `/asset/${encodedAssetId}/overview`,
      icon: Eye,
      enabled: true,
    },
    {
      name: "Asset Vitals",
      path: `/asset/${encodedAssetId}/vitals`,
      icon: Activity,
      enabled: true,
    },
    {
      name: "Trends",
      path: `/asset/${encodedAssetId}/trends`,
      icon: Lightbulb,
      enabled: true,
    },
    {
      name: "Alarms",
      path: `/asset/${encodedAssetId}/alarms`,
      icon: Bell,
      enabled: false,
    },
    {
      name: "Downtime Analysis",
      path: `/asset/${encodedAssetId}/downtime-analysis`,
      icon: SearchCheck,
      enabled: true,
    },
    {
      name: "Root Cause Analysis",
      path: `/asset/${encodedAssetId}/root-cause-analysis`,
      icon: SearchCheck,
      enabled: true,
    },
    {
      name: "Optimization",
      path: `/asset/${encodedAssetId}/optimization`,
      icon: Sparkles,
      enabled: true,
    },
  ];

  // Check if current location matches the tab path
  const isActive = (path) => {
    // Normalize paths (remove trailing slashes and query strings)
    const normalizedPath = path.replace(/\/$/, "").split("?")[0];
    const normalizedCurrent = currentPath.replace(/\/$/, "").split("?")[0];

    // Exact match
    if (normalizedCurrent === normalizedPath) {
      return true;
    }

    // Handle overview route - also match /asset/:id (without /overview)
    if (path.includes("/overview")) {
      const encodedBasePath = `/asset/${encodedAssetId}`;
      const decodedBasePath = `/asset/${assetId}`;
      return (
        normalizedCurrent === encodedBasePath
        || normalizedCurrent === decodedBasePath
      );
    }

    return false;
  };

  return (
    <nav className={segmentedTabTrack}>
      {navigationItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path);

        return item.enabled ? (
          <Link
            key={item.name}
            href={`${item.path}${queryString}`}
            className='block'>
            <div
              className={cn(
                "flex cursor-pointer items-center gap-2 whitespace-nowrap px-3 py-1.5 transition-colors",
                active ? segmentedTabSelected : segmentedTabIdle
              )}
              data-active={active}>
              {Icon && <Icon className='h-4 w-4' />}
              <span className='text-sm font-medium'>{item.name}</span>
            </div>
          </Link>
        ) : (
          <div
            key={item.name}
            className='flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-muted-foreground opacity-60'
            title='Coming soon'>
            {Icon && <Icon className='h-4 w-4' />}
            <span className='text-sm font-medium'>{item.name}</span>
          </div>
        );
      })}
    </nav>
  );
}
